import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from "react-router-dom";
import 'bootstrap/dist/css/bootstrap.min.css';
import { usePlantData } from '../../../components/data/PlantData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faFloppyDisk, faCirclePlus, faCircleMinus } from '@fortawesome/free-solid-svg-icons'
import SidebarPageLayout from '../../../components/layout/SidebarPageLayout';
import Marquee from '../../../components/layout/Marquee';
import ExportQueryOutput from '../../../components/reports/ExportQueryOutput';
import { downloadReportExport, fetchExportTemplates, saveBlobAsFile } from '../../../api/reports';
import { buildExportQueryParamsFromForm } from '../../../util/buildExportQueryParams';
import {
  aggregateOptionsForMetric,
  defaultAggregateFormValue,
  getExportTemplateFormConfig,
} from '../../../util/exportTemplateFormConfig';
import { toSlug } from '../../../util/strings';
import Breadcrumbs from '../../../components/layout/Breadcrumbs';
import {
  MAX_EXPORT_DAYS,
  clampExportDateRange,
  formatLocalISODate,
  getExportDateBounds,
  inclusiveDayCount,
} from '../../../util/exportDateRange';

function FormLabel({ children, required = false }) {
  return (
    <span className="form-label">
      {children}
      {required ? (
        <span className="form-label__required" title="Required" aria-label="required">
          {' '}*
        </span>
      ) : null}
    </span>
  );
}

function MetricFieldRow({
  metrics,
  typeSelectProps = {},
  fieldSelectProps = {},
  onRemove,
  showRemove = false,
}) {
  const [selectedMetricName, setSelectedMetricName] = useState(
    fieldSelectProps.defaultValue ?? '0',
  );

  const selectedMetric = metrics.find(
    (metric) => metric.name === selectedMetricName,
  );
  const aggregateOptions = aggregateOptionsForMetric(selectedMetric);

  return (
    <>
      <select
        className="form-select type-select"
        defaultValue={
          typeSelectProps.defaultValue
          ?? defaultAggregateFormValue(selectedMetric)
        }
        key={`${selectedMetricName}-${aggregateOptions.map((o) => o.formValue).join('-')}`}
      >
        <option value="0">Select Data Type</option>
        {aggregateOptions.map((option) => (
          <option key={option.formValue} value={option.formValue}>
            {option.label}
          </option>
        ))}
      </select>
      <select
        className="form-select field-select"
        defaultValue={fieldSelectProps.defaultValue ?? '0'}
        onChange={(event) => setSelectedMetricName(event.target.value)}
      >
        <option value="0">Select Data Point</option>
        {metrics.map((metric) => (
          <option key={metric.name} value={metric.name}>
            {metric.label}{metric.unit ? ` (${metric.unit})` : ''}
          </option>
        ))}
      </select>
      {showRemove ? (
        <button type="button" onClick={onRemove}>
          <FontAwesomeIcon icon={faCircleMinus} />
        </button>
      ) : (
        <span />
      )}
    </>
  );
}

function FilterRow({
  metrics,
  onRemove,
  showRemove = false,
}) {
  return (
    <>
      <select className="form-select filter-select">
        <option value="0">Select metric…</option>
        {metrics.map((metric) => (
          <option key={metric.name} value={metric.name}>
            {metric.label}{metric.unit ? ` (${metric.unit})` : ''}
          </option>
        ))}
      </select>
      <select className="form-select operator-select">
        <option value="0">Select…</option>
        <option value="1">=</option>
        <option value="2">&gt;</option>
        <option value="3">&lt;</option>
        <option value="4">&gt;=</option>
        <option value="5">&lt;=</option>
        <option value="6">&ne;</option>
      </select>
      <input type="number" placeholder="Enter…" className="form-control value-input" />
      {showRemove ? (
        <button type="button" onClick={onRemove}>
          <FontAwesomeIcon icon={faCircleMinus} />
        </button>
      ) : (
        <span />
      )}
    </>
  );
}

function DataExports() {
  const { plant: gemini } = usePlantData();

  const [extraFilters, setExtraFilters] = useState([]);
  const addFilter = () => {
    setExtraFilters((prev) => [...prev, { id: Date.now() + Math.random() }]);
  };
  const removeFilter = (id) => {
    setExtraFilters((prev) => prev.filter((f) => f.id !== id));
  };

  const [extraDataFields, setExtraDataFields] = useState([]);
  const addDataField = () => {
    setExtraDataFields((prev) => [...prev, { id: Date.now() + Math.random() }]);
  };
  const removeDataField = (id) => {
    setExtraDataFields((prev) => prev.filter((f) => f.id !== id));
  };

  const [exportOutput, setExportOutput] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportTemplates, setExportTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');

  const portfolioName = toSlug(gemini.plant_name) || 'gemini';
  const todayISO = formatLocalISODate();
  const dateBounds = getExportDateBounds(exportStartDate, exportEndDate, todayISO);

  const selectedTemplate = exportTemplates.find(
    (template) => template.id === selectedTemplateId,
  );

  const formConfig = useMemo(
    () => getExportTemplateFormConfig(selectedTemplate),
    [selectedTemplate],
  );

  const fieldReq = formConfig.fieldRequirements ?? {};

  const formRef = useRef(null);

  const handleTemplateChange = (event) => {
    setSelectedTemplateId(event.target.value);
    setExtraFilters([]);
    setExtraDataFields([]);
    setExportOutput(null);
    setExportStartDate('');
    setExportEndDate('');
  };

  const handleStartDateChange = (event) => {
    const next = clampExportDateRange(event.target.value, exportEndDate, todayISO);
    setExportStartDate(next.startDate);
    setExportEndDate(next.endDate);
    setExportOutput(null);
  };

  const handleEndDateChange = (event) => {
    const next = clampExportDateRange(exportStartDate, event.target.value, todayISO);
    setExportStartDate(next.startDate);
    setExportEndDate(next.endDate);
    setExportOutput(null);
  };

  const handleClear = () => {
    if (formRef.current) {
      formRef.current.reset();
    }
    setExtraFilters([]);
    setExtraDataFields([]);
    setExportOutput(null);
    setSelectedTemplateId('');
    setExportStartDate('');
    setExportEndDate('');
  };

  const handleExport = async () => {
    if (!formRef.current) {
      return;
    }

    if (!selectedTemplateId) {
      setExportOutput({ error: 'Choose a saved report.' });
      return;
    }

    if (fieldReq.sector) {
      const sectorEl = formRef.current.querySelector('#deviceGroups');
      if (!sectorEl?.value || sectorEl.value === '0') {
        setExportOutput({ error: 'Sector is required.' });
        return;
      }
    }

    if (!formRef.current.reportValidity()) {
      return;
    }

    if (
      exportStartDate
      && exportEndDate
      && (
        exportStartDate < dateBounds.earliest
        || exportEndDate < dateBounds.earliest
        || exportStartDate > todayISO
        || exportEndDate > todayISO
        || inclusiveDayCount(exportStartDate, exportEndDate) > MAX_EXPORT_DAYS
      )
    ) {
      setExportOutput({
        error: `Date range must fall within the last ${MAX_EXPORT_DAYS} days (through today).`,
      });
      return;
    }

    const queryParams = buildExportQueryParamsFromForm(formRef.current);

    setExportLoading(true);
    setExportOutput(null);

    try {
      const { blob, fileName, rowCount } = await downloadReportExport(portfolioName, queryParams);
      saveBlobAsFile(blob, fileName);
      setExportOutput({
        success: true,
        fileName,
        rowCount,
      });
    } catch (err) {
      setExportOutput({
        error: err.message || 'Export request failed',
      });
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Vivosol Plant Performance Insights Hub | Gemini / Reports / Data Exports";
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setTemplatesLoading(true);
      try {
        const data = await fetchExportTemplates(portfolioName);
        const templates = Array.isArray(data?.templates) ? data.templates : [];
        if (!cancelled) {
          setExportTemplates(templates);
        }
      } catch (err) {
        console.warn('Failed to load export templates:', err);
        if (!cancelled) {
          setExportTemplates([]);
        }
      } finally {
        if (!cancelled) {
          setTemplatesLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [portfolioName]);

  useEffect(() => {
    if (!formRef.current || !formConfig.hasTemplate) {
      return;
    }

    const intervalEl = formRef.current.querySelector('#interval');
    const aggregationEl = formRef.current.querySelector('#aggregation');
    if (intervalEl) {
      intervalEl.value = formConfig.defaultInterval;
    }
    if (aggregationEl) {
      aggregationEl.value = formConfig.defaultAggregation;
    }
  }, [selectedTemplateId, formConfig]);

  return (
    <SidebarPageLayout id="dataExports">
      <div className="content p-5">
        <Marquee />
        <h1 className="mt-3 mb-0"><Link to={`${gemini.dashboard}`}><strong>{gemini.plant_name}</strong></Link> : {gemini.config} ({gemini.location})</h1>
        <Breadcrumbs labels={{ portfolioName: gemini.plant_name }} />
        <div className="data-exports">
          <form ref={formRef} className="form d-flex flex-column gap-3" key={selectedTemplateId || 'no-template'}>
            <div className="form-group">
              <FormLabel required={fieldReq.template}>Choose Saved Report</FormLabel>
              <select
                id="template"
                className="form-select"
                value={selectedTemplateId}
                onChange={handleTemplateChange}
                disabled={templatesLoading}
                required
              >
                <option value="">
                  {templatesLoading ? 'Loading reports…' : 'Select report'}
                </option>
                {exportTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.label}
                  </option>
                ))}
              </select>
              <button type="button"><FontAwesomeIcon icon={faInfoCircle} /></button>
              <button type="button"><FontAwesomeIcon icon={faFloppyDisk} /></button>
            </div>

            {!formConfig.hasTemplate ? (
              <p className="text-muted mb-0">
                Select a report to see the export options for that chart.
              </p>
            ) : (
              <p className="text-muted small mb-0">
                Fields marked with <span className="form-label__required">*</span> are required.
                Interval, aggregation, and data points use report defaults when not changed.
              </p>
            )}

            {formConfig.showInterval ? (
              <div className="form-group">
                <FormLabel required={fieldReq.interval}>Data Interval</FormLabel>
                <select
                  id="interval"
                  className="form-select"
                  defaultValue={formConfig.defaultInterval}
                  required={fieldReq.interval}
                >
                  {formConfig.intervalOptions.length > 1 ? (
                    <option value="0">Select Interval</option>
                  ) : null}
                  {formConfig.intervalOptions.map((option) => (
                    <option key={option.formValue} value={option.formValue}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {formConfig.showAggregation ? (
              <div className="form-group">
                <FormLabel required={fieldReq.aggregation}>Aggregation Type</FormLabel>
                <select
                  id="aggregation"
                  className="form-select"
                  defaultValue={formConfig.defaultAggregation}
                  required={fieldReq.aggregation}
                >
                  {formConfig.aggregationOptions.length > 1 ? (
                    <option value="0">Select Aggregation Type</option>
                  ) : null}
                  {formConfig.aggregationOptions.map((option) => (
                    <option key={option.formValue} value={option.formValue}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {formConfig.showSector ? (
              <div className="form-group">
                <FormLabel required={fieldReq.sector}>Sector (Transformer)</FormLabel>
                <select id="deviceGroups" className="form-select" required>
                  <option value="0">Select Sector</option>
                  <option value="1">Transformer 1</option>
                  <option value="2">Transformer 2</option>
                  <option value="3">Transformer 3</option>
                  <option value="4">Transformer 4</option>
                </select>
              </div>
            ) : null}

            {formConfig.hasTemplate ? (
              <div className="form-group">
                <FormLabel required={fieldReq.startDate}>Start/End Date</FormLabel>
                <input
                  type="date"
                  id="start-date"
                  className="form-control"
                  value={exportStartDate}
                  onChange={handleStartDateChange}
                  min={dateBounds.startMin ?? undefined}
                  max={dateBounds.startMax}
                  required
                />
                <input
                  type="date"
                  id="end-date"
                  className="form-control"
                  value={exportEndDate}
                  onChange={handleEndDateChange}
                  min={dateBounds.endMin ?? undefined}
                  max={dateBounds.endMax}
                  required
                />
              </div>
            ) : null}

            {formConfig.hasTemplate ? (
              <p className="text-muted small mb-0">
                Select dates within the last {MAX_EXPORT_DAYS} days (through today).
              </p>
            ) : null}

            {formConfig.showCategory ? (
              <div className="form-group">
                <FormLabel required={fieldReq.category}>Resource Type</FormLabel>
                <select id="category" className="form-select" defaultValue={
                  selectedTemplate?.categoryDefault === 'device' ? '1'
                    : selectedTemplate?.categoryDefault === 'transformer' ? '2'
                      : '0'
                }>
                  <option value="0">Select Resource Type</option>
                  <option value="1">Device</option>
                  <option value="2">Transformer</option>
                </select>
              </div>
            ) : null}

            {formConfig.showFilters ? (
              <>
                <div className="form-group">
                  <FormLabel required={fieldReq.filters}>Data Filters</FormLabel>
                  <FilterRow metrics={formConfig.metrics} />
                  <button type="button" onClick={addFilter}>
                    <FontAwesomeIcon icon={faCirclePlus} />
                  </button>
                </div>
                {extraFilters.map((filter) => (
                  <div className="form-group" key={filter.id}>
                    <span className="form-label" />
                    <FilterRow
                      metrics={formConfig.metrics}
                      showRemove
                      onRemove={() => removeFilter(filter.id)}
                    />
                  </div>
                ))}
                <p className="text-muted small mb-0">
                  Optional — add filters to narrow exported rows.
                </p>
              </>
            ) : null}

            {formConfig.showDataPoints ? (
              <>
                <div className="form-group">
                  <FormLabel required={fieldReq.dataPoints}>Data Points</FormLabel>
                  <MetricFieldRow metrics={formConfig.metrics} />
                  <button type="button" onClick={addDataField}>
                    <FontAwesomeIcon icon={faCirclePlus} />
                  </button>
                </div>
                <p className="text-muted small mb-0">
                  Optional — leave unselected to export all metrics for this report.
                </p>
                {extraDataFields.map((field) => (
                  <div className="form-group" key={field.id}>
                    <span className="form-label" />
                    <MetricFieldRow
                      metrics={formConfig.metrics}
                      showRemove
                      onRemove={() => removeDataField(field.id)}
                    />
                  </div>
                ))}
              </>
            ) : null}

            {formConfig.hasTemplate ? (
              <div className="form-group">
                <span className="form-label">&nbsp;</span>
                <button
                  id="export-button"
                  type="button"
                  onClick={handleExport}
                  disabled={exportLoading}
                >
                  {exportLoading ? 'EXPORTING…' : 'EXPORT'}
                </button>
                <button id="clear-button" type="button" onClick={handleClear}>CLEAR</button>
              </div>
            ) : null}
          </form>
          <ExportQueryOutput output={exportOutput} loading={exportLoading} />
        </div>
      </div>
    </SidebarPageLayout>
  );
}

export default DataExports;
